import { UserLanguage } from '../../../../../features/account/types/User'
import { LanguageMaps } from '../../../../api/language'
import { ContentLanguageTranslation } from '../../../../types/InternalAnalystComment'
import { InternalCategoryFeature } from '../../../../types/InternalCategory'
import { categoryLanguages, languageEntry, readCategoryNames } from '../../helpers/categoryLanguageHelpers'
import {
  CategoryFeatureOrder,
  CategoryIcon,
  CategorySaveBody,
  CategorySaveResult,
  FeatureActiveBody,
  InternalCategoryRecord,
  LanguageValues,
} from '../../types/Categories'

/** Legacy's limit for uploaded images. */
export const maxIconBytes = 5 * 1024 * 1024

export const byLanguage = <Value>(read: (language: UserLanguage) => Value) =>
  Object.fromEntries(categoryLanguages.map((language) => [language, read(language)])) as LanguageValues<Value>

// Legacy's `assignTranslation`: null without an entry, and the entry's field (possibly undefined, so dropped from the JSON) with one.
export const assignTranslation = (languageMaps: LanguageMaps | undefined, group: string, key: number, field: string) =>
  byLanguage((language) => {
    const entry = languageEntry(languageMaps, language, group, key)
    return entry ? entry[field] : null
  })

/** Legacy's toggle body: the listed feature with `active` flipped and its names and choice labels re-sent from the language maps. */
export const toFeatureActiveBody = (feature: InternalCategoryFeature, languageMaps: LanguageMaps): FeatureActiveBody => {
  const names = assignTranslation(languageMaps, 'features', feature.legacyId, 'name')
  const alternateNames = assignTranslation(languageMaps, 'features', feature.legacyId, 'alternateName')
  return {
    ...feature,
    active: !feature.active,
    names,
    translations: byLanguage((language) => ({ name: names[language], alternateName: alternateNames[language] })),
    choices: feature.choices.map((choice) => {
      const labels = assignTranslation(languageMaps, 'choices', choice.weightId, 'label')
      return { ...choice, labels, translations: byLanguage((language) => ({ label: labels[language] })) }
    }),
  }
}

export const isFeatureActiveStored = (stored: InternalCategoryFeature[], body: FeatureActiveBody) =>
  stored.find((feature) => feature.id === body.id)?.active === body.active

export const sortFeatures = (features: InternalCategoryFeature[]) => [...features].sort((a, b) => a.ordr - b.ordr)

export const applyFeatureOrder = (features: InternalCategoryFeature[], orderedIds?: string[]) => {
  const sorted = sortFeatures(features)
  if (!orderedIds) return sorted
  const byId = new Map(sorted.map((feature) => [feature.id, feature]))
  const ordered = orderedIds.flatMap((id) => byId.get(id) ?? [])
  return [...ordered, ...sorted.filter((feature) => !orderedIds.includes(feature.id))]
}

/** Legacy sends every feature of the category, numbered from 1 in the shown order. */
export const toFeatureOrder = (features: InternalCategoryFeature[]): CategoryFeatureOrder =>
  features.map((feature, index) => ({ featureId: feature.legacyId, order: index + 1 }))

/** The features whose stored `ordr` isn't the one sent. */
export const getMisorderedFeatures = (order: CategoryFeatureOrder, stored: InternalCategoryFeature[]) =>
  order.flatMap(({ featureId, order: sentOrder }) => {
    const feature = stored.find((candidate) => candidate.legacyId === featureId)
    return !feature || feature.ordr !== sentOrder ? [feature ? featureLabel(feature) : String(featureId)] : []
  })

export const featureLabel = (feature: Pick<InternalCategoryFeature, 'legacyId' | 'name'>) => `${feature.legacyId} ${feature.name}`.trim()

export const getFeatureCounts = (features: InternalCategoryFeature[]) => ({
  total: features.length,
  active: features.filter((feature) => feature.active).length,
})

export const getChoiceNames = (feature: InternalCategoryFeature) => feature.choices.map((choice) => choice.choice)

type Named = { id: string; name: string }

export const getNamesByIds = (ids: string[], items: Named[] | undefined) => ids.map((id) => items?.find((item) => item.id === id)?.name ?? id)

/** A category with legacy id 0 shares its language entries with any new category, which the backend also gives legacy id 0. */
export const getLegacyIdZeroCategory = (categories: InternalCategoryRecord[]) => categories.find((category) => category.legacyId === 0)

export type CategoryFormValues = {
  names: ContentLanguageTranslation
  active: boolean
  icon: CategoryIcon | null
}

export const toCategoryFormValues = (category: InternalCategoryRecord | undefined, languageMaps: LanguageMaps | undefined): CategoryFormValues => {
  if (!category) return { names: {}, active: false, icon: null }
  const names = readCategoryNames(languageMaps, category.legacyId)
  return {
    names: byLanguage((language) => (language === 'en' ? names.en || category.name : names[language] ?? '')),
    active: !!category.active,
    icon: category.icon ?? null,
  }
}

/** Customer screens read `icon.url` of every active category. */
export const needsIcon = (values: Pick<CategoryFormValues, 'active' | 'icon'>) => values.active && !values.icon

export const toCategorySaveBody = (category: InternalCategoryRecord | undefined, values: CategoryFormValues): CategorySaveBody => {
  const names = byLanguage((language) => values.names[language] ?? '')
  const translations = byLanguage((language) => ({ name: names[language] }))
  if (!category) {
    return { name: names.en, names, translations, active: values.active, ...(values.icon ? { icon: values.icon } : {}) }
  }
  return { ...category, name: names.en, names, translations, active: values.active, icon: values.icon }
}

export type CategoryField = 'category' | 'name' | 'names' | 'active' | 'icon'

export const categoryFieldLabelKeys: Record<CategoryField, string> = {
  category: 'internal-analysts:categories_field_category',
  name: 'internal-analysts:categories_field_name',
  names: 'internal-analysts:categories_field_translations',
  active: 'internal-analysts:categories_field_active',
  icon: 'internal-analysts:categories_field_icon',
}

export const getUnstoredCategoryFields = (body: CategorySaveBody, { stored, storedNames }: CategorySaveResult): CategoryField[] => {
  if (!stored) return ['category']
  const fields: CategoryField[] = []
  if (stored.name !== body.name) fields.push('name')
  if (categoryLanguages.some((language) => (storedNames?.[language] ?? '') !== body.names[language])) fields.push('names')
  if (!!stored.active !== body.active) fields.push('active')
  if ((stored.icon?.id ?? null) !== (body.icon?.id ?? null)) fields.push('icon')
  return fields
}

/** Resolves the image's size, or undefined when it can't be read as an image. */
export const readImageSize = (file: Blob) =>
  new Promise<{ width: number; height: number } | undefined>((resolve) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve({ width: image.naturalWidth, height: image.naturalHeight })
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(undefined)
    }
    image.src = url
  })

export type IconFileProblem = 'type' | 'size' | 'unreadable' | 'notSquare'

export const getIconFileProblem = async (file: File, readSize = readImageSize): Promise<IconFileProblem | undefined> => {
  if (file.type !== 'image/png') return 'type'
  if (file.size > maxIconBytes) return 'size'
  const size = await readSize(file)
  if (!size) return 'unreadable'
  return size.width === size.height ? undefined : 'notSquare'
}
